import { EmptyState } from '@/components/EmptyState'
import {
  ArrowRight,
  AtSign,
  Bell,
  Building2,
  CheckCircle2,
  ClipboardList,
  Clock,
  Coins,
  FileText,
  FileUp,
  Gauge,
  Hourglass,
  Inbox,
  Layers,
  ListChecks,
  type LucideIcon,
  Mail,
  PenLine,
  RefreshCw,
  RotateCw,
  Sparkles,
  TriangleAlert,
  User,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { notificationTitle } from './notificationTitle'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { formatDatumTijd } from '@/lib/format'
import { DESKTOP_QUERY, useMediaQuery } from '@/lib/useMediaQuery'
import { cn } from '@/lib/utils'
import { type DayGroupLabel, dayGroupLabel, relativeTime } from './relativeTime'
import type { NotificationsContextValue } from './notificationsContext'
import { useNotificationsContext } from './useNotificationsContext'
import { NOTIFICATION_GROUPS, type Notification, type NotificationType } from './types'

/** Muted lucide icon per type; unknown (future) types fall back to the bell. */
const TYPE_ICONS: Record<NotificationType, LucideIcon> = {
  startup_created: Building2,
  mention: AtSign,
  task_assigned: ClipboardList,
  deal_phase_changed: ArrowRight,
  deal_status_changed: RefreshCw,
  deal_owner_changed: User,
  nda_signed: PenLine,
  deal_stalled: Hourglass,
  startup_returned_after_rejection: RotateCw,
  deals_bulk_updated: Layers,
  startup_returned: RotateCw,
  leads_imported: FileUp,
  ai_run_completed: Sparkles,
  form_submitted: FileText,
  financial_dd_submitted: Coins,
  document_uploaded: FileUp,
  task_due_soon: Clock,
  task_overdue: TriangleAlert,
  mail_received: Mail,
  portal_form_submitted: Inbox,
  portal_milestone_ticked: ListChecks,
  wasstraat_item_completed: CheckCircle2,
  ai_budget_threshold: Gauge,
}

/** Dossier tab hosting each AI run kind (spec-34 tab set), for the deep-link. */
const AI_KIND_TABS: Record<string, string> = {
  gap_analysis: 'gap-analyse',
  ic_scoring: 'ic',
  deep_research: 'due-diligence',
  claims_dd: 'due-diligence',
  financial_dd_validation: 'due-diligence',
  followups: 'formulieren',
  journey_seed: 'journey',
  journey_narrative: 'journey',
  notulen_analysis: 'notulen',
}

/** Dossier tab per founder-action/mail type, for the deep-link. */
const DOSSIER_TAB_BY_TYPE: Partial<Record<NotificationType, string>> = {
  form_submitted: 'formulieren',
  financial_dd_submitted: 'due-diligence',
  document_uploaded: 'documenten',
  mail_received: 'mail',
}

/** spec-61 C3: a bulk summary opens the tab where its deals now are; the rest the overview. */
const BULK_LINKS = new Map<string, string>([
  ['reject', '/preinvest/dossiers?tab=afgewezen'],
  ['rejection_reason', '/preinvest/dossiers?tab=afgewezen'],
  ['park', '/preinvest/dossiers?tab=geparkeerd'],
  ['assign', '/preinvest/dossiers?tab=leads'],
  ['classify', '/preinvest/dossiers?tab=leads'],
])

/**
 * Deep-link per type (spec-35 §Frontend). Founder-action/mail/AI events target
 * the dossier tab that hosts the content (`?tab=`, spec-34); deal-lifecycle
 * lands on the deal-hero; task types keep the spec-21 wasstraat → deal → /taken
 * chain; post-invest events go to the wasstraat dossier when the item is known.
 */
function notificationLink(notification: Notification): string | null {
  const payload = notification.payload
  switch (notification.type) {
    case 'portal_milestone_ticked':
      // spec-74 B8: the dossier tab "Doelen & milestones".
      return payload.wasstraat_item_id
        ? `/postinvest/wasstraat/${payload.wasstraat_item_id}?tab=doelen`
        : '/postinvest'
    case 'portal_form_submitted':
    case 'wasstraat_item_completed':
      return payload.wasstraat_item_id
        ? `/postinvest/wasstraat/${payload.wasstraat_item_id}`
        : '/postinvest'
    case 'ai_run_completed': {
      if (!payload.deal_id) return null
      const tab = payload.kind ? AI_KIND_TABS[payload.kind] : undefined
      return tab
        ? `/preinvest/deals/${payload.deal_id}?tab=${tab}`
        : `/preinvest/deals/${payload.deal_id}`
    }
    case 'form_submitted':
    case 'financial_dd_submitted':
    case 'document_uploaded':
    case 'mail_received':
      return payload.deal_id
        ? `/preinvest/deals/${payload.deal_id}?tab=${DOSSIER_TAB_BY_TYPE[notification.type]}`
        : null
    case 'ai_budget_threshold':
      // spec-78: the usage page, on the month the alert is about.
      return payload.month
        ? `/preinvest/ai-verbruik?maand=${payload.month}`
        : '/preinvest/ai-verbruik'
    case 'leads_imported':
      // One summary for a whole list (spec-60 B2): the Leads tab filtered on this import.
      return payload.batch_id
        ? `/preinvest/dossiers?tab=leads&import=${payload.batch_id}`
        : '/preinvest/dossiers?tab=leads'
    case 'deals_bulk_updated':
      // One summary for many deals (spec-60): no single deal to open, but the tab
      // the deals are in now (spec-61 C3).
      return (payload.action && BULK_LINKS.get(payload.action)) || '/preinvest/dossiers'
    default:
      // Existing chain (spec-21 D4), also the fallback for deal-lifecycle and
      // unknown types: wasstraat task → onboarding dossier, deal-linked → deal
      // dossier, task types without a platform link → unified task overview.
      if (payload.wasstraat_item_id) return `/postinvest/wasstraat/${payload.wasstraat_item_id}`
      if (payload.deal_id) return `/preinvest/deals/${payload.deal_id}`
      // spec-63 E4: a relation task opens its relation (wasstraat → deal → relatie → /taken).
      if (payload.organisation_id) return `/relaties/organisaties/${payload.organisation_id}?tab=activiteit`
      if (payload.person_id) return `/relaties/contacten/${payload.person_id}?tab=activiteit`
      if (
        notification.type === 'task_assigned' ||
        notification.type === 'task_due_soon' ||
        notification.type === 'task_overdue'
      ) {
        return '/taken'
      }
      return null
  }
}

/** Group the (newest-first) list into consecutive Vandaag/Gisteren/Ouder runs. */
function groupByDay(
  items: Notification[],
  now: Date,
): { label: DayGroupLabel; items: Notification[] }[] {
  const groups: { label: DayGroupLabel; items: Notification[] }[] = []
  for (const item of items) {
    const label: DayGroupLabel = dayGroupLabel(item.created_at, now)
    const current = groups[groups.length - 1]
    if (current && current.label === label) current.items.push(item)
    else groups.push({ label, items: [item] })
  }
  return groups
}

/**
 * Bell + inbox popover (spec-35 phase 5, reworked in spec-64 U-104/U-043). Shows an
 * unread badge and opens a non-modal panel next to the sidebar: filter chips on one
 * scrollable row (Alles + the type groups; a chip refetches the list via `?types=`),
 * the caller's notifications newest-first grouped per day (Vandaag / Gisteren / Ouder),
 * each an open button (marks read and deep-links) with its own delete button beside
 * it. From md up the panel stays open after opening a notification; on a phone it is a
 * full-width panel that closes. "Markeer alles als gelezen" clears the badge, which
 * always counts every unread notification regardless of the active chip, and "Gelezen
 * wissen" clears the read ones of the active filter, with undo (spec-35, spec-64-u2).
 */
function NotificationBellView({
  items,
  unreadCount,
  setTypeFilter,
  markRead,
  markAllRead,
  remove,
  clearRead,
  refetch,
}: NotificationsContextValue) {
  // Per-instance UI state on purpose: when two bells are mounted (desktop aside
  // + mobile topbar) only one is visible, and each must own its own popover.
  const [open, setOpen] = useState(false)
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const navigate = useNavigate()
  const desktop = useMediaQuery(DESKTOP_QUERY)
  const { membersById } = useTeamMembers()
  const ownerName = (id: string) => membersById?.get(id)?.display_name ?? null

  function onOpenChange(next: boolean) {
    setOpen(next)
    if (next) void refetch()
  }

  function onSelectGroup(key: string | null) {
    setActiveGroup(key)
    const group = NOTIFICATION_GROUPS.find((candidate) => candidate.key === key)
    setTypeFilter(group ? group.types : null)
  }

  function onOpenNotification(notification: Notification) {
    void markRead(notification.id)
    const link = notificationLink(notification)
    if (link) navigate(link)
    // Desktop keeps the panel for the next one; the phone panel covers the page.
    if (!desktop) setOpen(false)
  }

  const dayGroups = groupByDay(items, new Date())

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Meldingen"
          aria-label={unreadCount > 0 ? `Meldingen, ${unreadCount} ongelezen` : 'Meldingen'}
          className="relative shrink-0 rounded-md p-2 max-md:p-3 text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <Bell className="size-[18px]" />
          {unreadCount > 0 ? (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-sidebar-primary px-1 text-xs font-bold leading-4 text-sidebar-primary-foreground"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      {/* 520px does not fit a phone, so the panel takes the screen width there and
          sits under the topbar; from md up it sits right of the sidebar. A fixed
          height keeps the panel the same size whether it holds 0 or 40 items. */}
      <PopoverContent
        side={desktop ? 'right' : 'bottom'}
        align={desktop ? 'start' : 'end'}
        sideOffset={12}
        collisionPadding={12}
        className="flex h-[min(560px,calc(100dvh-6rem))] w-[calc(100vw-1.5rem)] flex-col overflow-hidden p-0 md:w-[520px]"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4">
          <h2 className="text-base font-semibold">Meldingen</h2>
          <div className="flex items-center">
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="px-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
              >
                Markeer alles als gelezen
              </button>
            ) : null}
            {items.some((row) => row.read_at !== null) ? (
              <button
                type="button"
                onClick={() => clearRead()}
                className="px-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
              >
                Gelezen wissen
              </button>
            ) : null}
          </div>
        </div>
        <div
          data-testid="notification-filters"
          className="flex shrink-0 flex-wrap gap-1.5 border-b border-border px-4 pb-3 pt-3"
        >
          {[{ key: null as string | null, label: 'Alles' }, ...NOTIFICATION_GROUPS].map(
            (group) => {
              const active = activeGroup === group.key
              return (
                <button
                  key={group.key ?? 'alles'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onSelectGroup(group.key)}
                  className={cn(
                    'shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {group.label}
                </button>
              )
            },
          )}
        </div>
        {items.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
          <EmptyState
            variant="bare"
            size="compact"
            mascot={false}
            title={activeGroup ? 'Geen meldingen in dit filter' : 'Geen meldingen'}
          />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto pb-1">
            {dayGroups.map((group, groupIndex) => (
              <section key={`${group.label}-${groupIndex}`}>
                <h3 className="px-3 pb-0.5 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </h3>
                <ul>
                  {group.items.map((notification) => {
                    const Icon = TYPE_ICONS[notification.type] ?? Bell
                    const unread = notification.read_at === null
                    return (
                      <li
                        key={notification.id}
                        className={cn('flex items-start gap-1 pr-2', unread && 'bg-accent/40')}
                      >
                        <button
                          type="button"
                          title={formatDatumTijd(notification.created_at)}
                          onClick={() => onOpenNotification(notification)}
                          className={cn(
                            'flex min-w-0 flex-1 items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                            unread ? 'font-semibold' : 'font-normal',
                          )}
                        >
                          <Icon
                            aria-hidden
                            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                          />
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="flex w-full items-start gap-2">
                              {unread ? (
                                <span
                                  aria-hidden
                                  className="mt-1.5 size-2 shrink-0 rounded-full bg-primary"
                                />
                              ) : null}
                              <span className="line-clamp-2">
                                {notificationTitle(notification, ownerName)}
                              </span>
                            </span>
                            <span
                              className="text-xs font-normal text-muted-foreground"
                              title={formatDatumTijd(notification.created_at)}
                            >
                              {notification.actor_name
                                ? `door ${notification.actor_name} · `
                                : ''}
                              {relativeTime(notification.created_at)}
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          aria-label="Melding verwijderen"
                          onClick={() => void remove(notification.id)}
                          // 32px from md up, 44px on a phone (the old row tap area moved here).
                          className="mt-1 flex size-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground max-md:size-11"
                        >
                          <X aria-hidden className="size-3.5" />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Bell that reads the inbox state from `<NotificationsProvider>`. The
 * responsive workspace shell mounts two bells (desktop aside + mobile topbar)
 * of which CSS shows one; both read the same provider so the page still
 * fetches and subscribes exactly once.
 */
export function NotificationBell() {
  const notifications = useNotificationsContext()
  return <NotificationBellView {...notifications} />
}
